import { supabase } from './supabase';
import { calculateFromTTC } from '@/utils/taxCalculator';

// Types pour les bons de commande (adaptés à la structure existante)
export interface Order {
  id: string;
  order_number?: string;
  client_id?: string;
  vendor_id?: string;
  store_id?: string;
  order_type: string;
  order_date: string;
  delivery_date?: string;
  delivery_info?: any; // JSON avec les informations de livraison
  total_htva: number;
  total_tva: number;
  total_ttc: number;
  status: string;
  created_at: string;
  updated_at?: string;
}

export interface OrderItem {
  id: string;
  order_id?: string;
  article_id?: string;
  quantity: number;
  unit_price: number;
  discount?: number;
  total_price: number;
  tax_rate?: number;
}

export interface OrderWithClient {
  id: string;
  order_number?: string;
  legacy_order_number?: string;
  order_date: string;
  delivery_date?: string;
  status: string;
  subtotal_ht: number;
  total_tax: number;
  total_ttc: number;
  created_at: string;
  order_type: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_postal_code?: string;
  client_city?: string;
  items_count: number;
  paid_amount: number;
  remaining_amount: number;
  total_acomptes?: number;
  store_id?: string;
  store_name?: string;
  store_initiales?: string;
  is_invoiced?: boolean; // Indique si la commande est facturée
}

export interface OrderItemDetail {
  id: string;
  order_id: string;
  order_number?: string;
  quantity: number;
  unit_price: number;
  discount?: number;
  tax_rate?: number;
  total_price: number;
  tax_amount?: number;
  total_with_tax?: number;
  article_code?: string;
  article_reference?: string;
  article_name?: string;
  article_description?: string;
}

export interface CreateOrderData {
  client_id?: string;
  vendor_id?: string;
  store_id?: string;
  order_type: string;
  order_date?: string;
  delivery_date?: string;
  status?: string;
}

export interface UpdateOrderData {
  client_id?: string;
  vendor_id?: string;
  store_id?: string;
  order_type?: string;
  order_date?: string;
  delivery_date?: string | null; // Peut être null pour vider le champ
  delivery_info?: any; // JSON avec les informations de livraison
  status?: string;
}

export interface CreateOrderItemData {
  order_id: string;
  article_id?: string; // Temporairement nullable jusqu'à correction du schéma DB
  quantity: number;
  unit_price: number;
  tax_rate?: number;
  // Nouvelles colonnes pour stocker les infos de l'article
  article_name?: string;
  article_code?: string;
  article_reference?: string;
  article_description?: string;
  // Colonnes de totaux selon le schéma DB
  line_total_ht?: number;
  line_total_tax?: number;
  line_total_ttc?: number;
  // Colonne total_price pour compatibilité
  total_price?: number;
  discount?: number;
}

export interface UpdateOrderItemData {
  quantity?: number;
  unit_price?: number;
  discount?: number;
  tax_rate?: number;
  total_price?: number;
  // Informations de l'article pour affichage
  article_name?: string;
  article_code?: string;
  article_reference?: string;
  article_description?: string;
}

export interface OrdersPaginatedResponse {
  orders: OrderWithClient[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

class OrderService {
  // =====================================================
  // GESTION DES COMMANDES
  // =====================================================

  /**
   * Récupérer les commandes avec pagination et recherche (version ultra-optimisée)
   */
  async getOrdersPaginated(
    page: number = 1,
    limit: number = 10,
    searchTerm: string = '',
    storeFilter?: string
  ): Promise<OrdersPaginatedResponse> {
    try {
      // Requête unique optimisée avec tous les calculs côté base de données
      let query = supabase
        .from('orders')
        .select(
          `
          id,
          order_number,
          legacy_order_number,
          order_date,
          delivery_date,
          delivery_info,
          status,
          total_htva,
          total_tva,
          total_ttc,
          created_at,
          order_type,
          store_id,
          vendor_id,
          stores:store_id (
            name,
            initiales
          ),
          vendors:vendor_id (
            name,
            initiales
          ),
          clients:client_id (
            full_name,
            email,
            phone,
            postal_code,
            city
          ),
          order_items(count),
          payments(amount)
        `,
          { count: 'exact' }
        )
        .eq('order_type', 'BONS DE COMMANDE');

      // Filtre par point de vente si spécifié
      if (storeFilter) {
        query = query.eq('store_id', storeFilter);
      }

      // Exécution de la requête avec ordre par date de création (plus rapide avec index)
      // Note: On ne fait pas la pagination ici si on a un terme de recherche
      // car on doit filtrer côté client pour les champs de relation
      const shouldFilterClientSide = searchTerm && searchTerm.trim();

      let data: any[];
      let count: number | null;
      let error: any;

      if (shouldFilterClientSide) {
        // Récupérer plus de données pour filtrer côté client
        const result = await query.order('created_at', { ascending: false }).limit(1000); // Limite raisonnable

        data = result.data || [];
        error = result.error;
        count = null; // On calculera après filtrage
      } else {
        // Pagination normale sans recherche
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        const result = await query.order('created_at', { ascending: false }).range(from, to);

        data = result.data || [];
        error = result.error;
        count = result.count;
      }

      if (error) {
        console.error('Erreur lors de la récupération des commandes:', error);
        throw error;
      }

      // Récupérer toutes les factures pour vérifier quelles commandes sont facturées
      // On fait une requête séparée pour éviter les problèmes de jointure
      const { data: invoicesData } = await supabase
        .from('invoices')
        .select('order_id')
        .not('order_id', 'is', null);

      // Créer un Set des IDs de commandes facturées pour une recherche rapide
      const invoicedOrderIds = new Set(
        (invoicesData || []).map((inv: any) => inv.order_id).filter(Boolean)
      );

      // Transformation optimisée des données
      let transformedData: OrderWithClient[] = (data || []).map((order: any) => {
        // Calculer le total des acomptes depuis les paiements
        const totalAcomptes =
          order.payments?.reduce((sum: number, payment: any) => sum + (payment.amount || 0), 0) ||
          0;

        // Vérifier si la commande est facturée
        const isInvoiced = invoicedOrderIds.has(order.id);

        return {
          id: order.id,
          order_number: order.order_number,
          legacy_order_number: order.legacy_order_number,
          order_date: order.order_date,
          delivery_date: order.delivery_date,
          delivery_info: order.delivery_info,
          status: order.status,
          subtotal_ht: order.total_htva,
          total_tax: order.total_tva,
          total_ttc: order.total_ttc,
          created_at: order.created_at,
          order_type: order.order_type,
          client_name: order.clients?.full_name || 'Client inconnu',
          client_email: order.clients?.email || '',
          client_phone: order.clients?.phone || '',
          client_postal_code: order.clients?.postal_code || '',
          client_city: order.clients?.city || '',
          items_count: order.order_items?.[0]?.count || 0, // Comptage direct depuis la requête
          paid_amount: totalAcomptes,
          remaining_amount: order.total_ttc - totalAcomptes,
          total_acomptes: totalAcomptes,
          store_id: order.store_id,
          store_name: order.stores?.name,
          store_initiales: order.stores?.initiales,
          vendor_id: order.vendor_id,
          vendor_name: order.vendors?.name,
          vendor_initiales: order.vendors?.initiales,
          is_invoiced: isInvoiced,
        };
      });

      // Filtrage côté client si terme de recherche
      if (shouldFilterClientSide && searchTerm) {
        const term = searchTerm.trim().toLowerCase();

        transformedData = transformedData.filter(order => {
          return (
            order.order_number?.toLowerCase().includes(term) ||
            String(order.legacy_order_number ?? '')
              .toLowerCase()
              .includes(term) ||
            order.client_name?.toLowerCase().includes(term) ||
            order.client_phone?.toLowerCase().includes(term) ||
            order.client_postal_code?.toLowerCase().includes(term) ||
            order.client_city?.toLowerCase().includes(term) ||
            order.total_ttc?.toString().includes(term)
          );
        });

        // Recalculer count et pagination après filtrage
        count = transformedData.length;

        // Appliquer la pagination sur les résultats filtrés
        const from = (page - 1) * limit;
        const to = from + limit;
        transformedData = transformedData.slice(from, to);
      }

      const totalPages = Math.ceil((count || 0) / limit);

      return {
        orders: transformedData,
        totalCount: count || 0,
        currentPage: page,
        totalPages,
      };
    } catch (error) {
      console.error('Erreur dans getOrdersPaginated:', error);
      throw error;
    }
  }

  /**
   * Récupérer une commande par ID
   */
  async getOrderById(id: string): Promise<Order | null> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(
          'id,order_number,client_id,store_id,order_type,order_date,delivery_date,delivery_info,total_htva,total_tva,total_ttc,status,created_at'
        )
        .eq('id', id)
        .single();
      const endTime = performance.now();
      void endTime; // perf measurement kept for future use

      if (error) {
        console.error('Erreur lors de la récupération de la commande:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erreur dans getOrderById:', error);
      throw error;
    }
  }

  /**
   * Récupérer une commande avec détails client
   */
  async getOrderWithClientById(id: string): Promise<OrderWithClient | null> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(
          `
          *,
          clients:client_id (
            id,
            full_name,
            email,
            phone,
            postal_code,
            city
          ),
          payments(amount)
        `
        )
        .eq('id', id)
        .single();

      if (error) {
        console.error('Erreur lors de la récupération de la commande avec client:', error);
        throw error;
      }

      if (!data) return null;

      // Calculer le total des acomptes depuis les paiements
      const totalAcomptes =
        data.payments?.reduce((sum: number, payment: any) => sum + (payment.amount || 0), 0) || 0;

      // Transformer les données pour correspondre à OrderWithClient
      const transformedData: OrderWithClient = {
        id: data.id,
        order_number: data.order_number, // Utiliser le vrai numéro de commande de la base
        order_date: data.order_date,
        delivery_date: data.delivery_date,
        status: data.status,
        subtotal_ht: data.total_htva,
        total_tax: data.total_tva,
        total_ttc: data.total_ttc,
        created_at: data.created_at,
        order_type: data.order_type,
        client_name: data.clients?.full_name || 'Client inconnu',
        client_email: data.clients?.email || '',
        client_phone: data.clients?.phone || '',
        client_postal_code: data.clients?.postal_code || '',
        client_city: data.clients?.city || '',
        items_count: 0, // À calculer si nécessaire
        paid_amount: totalAcomptes,
        remaining_amount: data.total_ttc - totalAcomptes,
        total_acomptes: totalAcomptes,
      };

      return transformedData;
    } catch (error) {
      console.error('Erreur dans getOrderWithClientById:', error);
      throw error;
    }
  }

  /**
   * Créer une nouvelle commande
   */
  async createOrder(orderData: CreateOrderData): Promise<Order | null> {
    try {
      const dataToInsert = {
        ...orderData,
        order_type: orderData.order_type || 'BONS DE COMMANDE',
        cloning_disabled: false,
      };

      const { data, error } = await supabase
        .from('orders')
        .insert([dataToInsert])
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la création de la commande:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erreur dans createOrder:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour une commande
   */
  async updateOrder(id: string, orderData: UpdateOrderData): Promise<Order | null> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .update(orderData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la mise à jour de la commande:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erreur dans updateOrder:', error);
      throw error;
    }
  }

  /**
   * Supprimer une commande et toutes ses données liées
   */
  async deleteOrder(id: string): Promise<boolean> {
    try {
      console.log('🗑️ Suppression transactionnelle de la commande:', id);

      const { error } = await supabase.rpc('delete_order', {
        p_order_id: id,
      });

      if (error) {
        console.error('❌ Erreur lors de la suppression de la commande:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur dans deleteOrder:', error);
      throw error;
    }
  }

  // =====================================================
  // GESTION DES LIGNES DE COMMANDE
  // =====================================================

  /**
   * Récupérer les lignes d'une commande
   */
  async getOrderItems(orderId: string): Promise<OrderItemDetail[]> {
    try {
      const { data, error } = await supabase
        .from('order_items')
        .select(
          'id,order_id,quantity,unit_price,discount,tax_rate,total_price,article_code,article_reference,article_name,article_description,created_at'
        )
        .eq('order_id', orderId)
        .order('created_at', { ascending: true }); // Trier par created_at ASC (premier article ajouté en haut)

      if (error) {
        console.error('❌ Erreur lors de la récupération des lignes de commande:', error);
        throw error;
      }

      // Transformer les données pour correspondre à OrderItemDetail
      const transformedData: OrderItemDetail[] = (data || []).map((item: any) => ({
        id: item.id,
        order_id: item.order_id,
        order_number: orderId,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount || 0,
        tax_rate: item.tax_rate || 21,
        total_price: item.total_price || item.line_total_ttc || item.quantity * item.unit_price,
        tax_amount:
          item.line_total_tax ||
          calculateFromTTC(
            item.total_price || item.line_total_ttc || item.quantity * item.unit_price,
            item.tax_rate || 21
          ).tva,
        total_with_tax: item.line_total_ttc || item.total_price || item.quantity * item.unit_price,
        article_code: item.article_code || '',
        article_reference: item.article_reference || '',
        article_name: item.article_name || 'Article à définir',
        article_description: item.article_description || '',
      }));

      return transformedData;
    } catch (error) {
      console.error('Erreur dans getOrderItems:', error);
      throw error;
    }
  }

  /**
   * Ajouter une ligne à une commande
   */
  async addOrderItem(itemData: CreateOrderItemData): Promise<OrderItem | null> {
    try {
      // Get next position for this order
      const { data: existingItems } = await supabase
        .from('order_items')
        .select('position')
        .eq('order_id', itemData.order_id)
        .order('position', { ascending: false })
        .limit(1);

      const maxPosition = existingItems?.[0]?.position ?? 0;
      const nextPosition = maxPosition + 1;

      const { data, error } = await supabase
        .from('order_items')
        .insert([{ ...itemData, position: nextPosition }])
        .select()
        .single();

      if (error) {
        console.error("❌ Erreur lors de l'ajout de la ligne de commande:", error);
        throw error;
      }

      // Recalculer et mettre à jour les totaux de la commande
      await this.updateOrderTotals(itemData.order_id);

      return data;
    } catch (error) {
      console.error('Erreur dans addOrderItem:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour les positions des articles (drag & drop)
   */
  async updateItemPositions(items: { id: string; position: number }[]): Promise<void> {
    const updates = items.map(item =>
      supabase.from('order_items').update({ position: item.position }).eq('id', item.id)
    );
    await Promise.all(updates);
  }

  /**
   * Mettre à jour une ligne de commande
   */
  async updateOrderItem(id: string, itemData: UpdateOrderItemData): Promise<OrderItem | null> {
    try {
      const { data, error } = await supabase
        .from('order_items')
        .update(itemData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la mise à jour de la ligne de commande:', error);
        throw error;
      }

      // Recalculer et mettre à jour les totaux de la commande
      if (data?.order_id) {
        await this.updateOrderTotals(data.order_id);
      }

      return data;
    } catch (error) {
      console.error('Erreur dans updateOrderItem:', error);
      throw error;
    }
  }

  /**
   * Supprimer une ligne de commande
   */
  async deleteOrderItem(id: string): Promise<boolean> {
    try {
      // Récupérer l'order_id avant suppression pour recalculer les totaux
      const { data: itemToDelete, error: fetchError } = await supabase
        .from('order_items')
        .select('order_id')
        .eq('id', id)
        .single();

      if (fetchError || !itemToDelete) {
        console.error("❌ Erreur lors de la récupération de l'article à supprimer:", fetchError);
        throw fetchError || new Error('Article à supprimer introuvable');
      }

      // Supprimer l'article
      const { data: deletedData, error: deleteError } = await supabase
        .from('order_items')
        .delete()
        .eq('id', id)
        .select();

      if (deleteError) {
        console.error('❌ Erreur lors de la suppression de la ligne de commande:', deleteError);
        throw deleteError;
      }

      // Vérifier si des lignes ont été supprimées
      if (!deletedData || deletedData.length === 0) {
        throw new Error(
          `Aucune ligne supprimée - l'article avec l'ID ${id} n'a pas pu être supprimé`
        );
      }

      // Recalculer et mettre à jour les totaux de la commande
      if (itemToDelete.order_id) {
        await this.updateOrderTotals(itemToDelete.order_id);
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur dans deleteOrderItem:', error);
      throw error;
    }
  }

  // =====================================================
  // FONCTIONS UTILITAIRES
  // =====================================================

  /**
   * Recalculer et mettre à jour les totaux d'une commande
   */
  async updateOrderTotals(orderId: string): Promise<void> {
    try {
      console.log('🔄 Recalcul des totaux pour la commande:', orderId);

      const { error } = await supabase.rpc('recalculate_order_totals', {
        p_order_id: orderId,
      });

      if (error) {
        console.error('Erreur lors du recalcul des totaux:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erreur dans updateOrderTotals:', error);
      throw error;
    }
  }

  /**
   * Changer le statut d'une commande
   */
  async updateOrderStatus(id: string, status: string): Promise<Order | null> {
    return this.updateOrder(id, { status });
  }

  /**
   * Récupérer les commandes par statut
   */
  async getOrdersByStatus(status: string): Promise<OrderWithClient[]> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(
          `
          *,
          clients:client_id (
            id,
            full_name,
            email,
            phone,
            postal_code,
            city
          ),
          payments(amount)
        `
        )
        .eq('status', status)
        .eq('order_type', 'BONS DE COMMANDE')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors de la récupération des commandes par statut:', error);
        throw error;
      }

      // Transformer les données pour correspondre à OrderWithClient
      const transformedData: OrderWithClient[] = (data || []).map((order: any) => {
        const totalAcomptes =
          order.payments?.reduce((sum: number, payment: any) => sum + (payment.amount || 0), 0) ||
          0;

        return {
          id: order.id,
          order_number: order.id,
          order_date: order.order_date,
          delivery_date: order.delivery_date,
          status: order.status,
          subtotal_ht: order.total_htva,
          total_tax: order.total_tva,
          total_ttc: order.total_ttc,
          created_at: order.created_at,
          order_type: order.order_type,
          client_name: order.clients?.full_name || 'Client inconnu',
          client_email: order.clients?.email || '',
          client_phone: order.clients?.phone || '',
          client_postal_code: order.clients?.postal_code || '',
          client_city: order.clients?.city || '',
          items_count: 0,
          paid_amount: totalAcomptes,
          remaining_amount: order.total_ttc - totalAcomptes,
          total_acomptes: totalAcomptes,
        };
      });

      return transformedData;
    } catch (error) {
      console.error('Erreur dans getOrdersByStatus:', error);
      throw error;
    }
  }

  /**
   * Récupérer les commandes d'un client
   */
  async getOrdersByClient(clientId: string): Promise<OrderWithClient[]> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(
          `
          *,
          clients:client_id (
            id,
            full_name,
            email,
            phone,
            postal_code,
            city
          ),
          payments(amount)
        `
        )
        .eq('client_id', clientId)
        .eq('order_type', 'BONS DE COMMANDE')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors de la récupération des commandes du client:', error);
        throw error;
      }

      // Transformer les données pour correspondre à OrderWithClient
      const transformedData: OrderWithClient[] = (data || []).map((order: any) => {
        const totalAcomptes =
          order.payments?.reduce((sum: number, payment: any) => sum + (payment.amount || 0), 0) ||
          0;

        return {
          id: order.id,
          order_number: order.id,
          order_date: order.order_date,
          delivery_date: order.delivery_date,
          status: order.status,
          subtotal_ht: order.total_htva,
          total_tax: order.total_tva,
          total_ttc: order.total_ttc,
          created_at: order.created_at,
          order_type: order.order_type,
          client_name: order.clients?.full_name || 'Client inconnu',
          client_email: order.clients?.email || '',
          client_phone: order.clients?.phone || '',
          client_postal_code: order.clients?.postal_code || '',
          client_city: order.clients?.city || '',
          items_count: 0,
          paid_amount: totalAcomptes,
          remaining_amount: order.total_ttc - totalAcomptes,
          total_acomptes: totalAcomptes,
        };
      });

      return transformedData;
    } catch (error) {
      console.error('Erreur dans getOrdersByClient:', error);
      throw error;
    }
  }

  /**
   * Calculer les statistiques des commandes
   */
  async getOrdersStats(): Promise<{
    total: number;
    brouillon: number;
    confirme: number;
    livre: number;
    facture: number;
    totalAmount: number;
  }> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('status, total_ttc')
        .eq('order_type', 'BONS DE COMMANDE');

      if (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
        throw error;
      }

      const stats = {
        total: data?.length || 0,
        brouillon: 0,
        confirme: 0,
        livre: 0,
        facture: 0,
        totalAmount: 0,
      };

      data?.forEach(order => {
        const status = order.status?.toLowerCase();
        if (status === 'brouillon') stats.brouillon++;
        else if (status === 'confirme') stats.confirme++;
        else if (status === 'livre') stats.livre++;
        else if (status === 'facture') stats.facture++;

        stats.totalAmount += order.total_ttc || 0;
      });

      return stats;
    } catch (error) {
      console.error('Erreur dans getOrdersStats:', error);
      throw error;
    }
  }
}

export const orderService = new OrderService();
